import React, { useCallback, useRef } from 'react';
import { GripHorizontal, Maximize2, Minimize2, X } from 'lucide-react';
import { WorkspaceWindowModel } from './types';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';

const accentBorder: Record<string, string> = {
  cyan: 'border-cyan-500/25 shadow-[0_0_40px_rgba(34,211,238,0.08)]',
  blue: 'border-blue-500/25 shadow-[0_0_40px_rgba(59,130,246,0.08)]',
  purple: 'border-purple-500/25 shadow-[0_0_40px_rgba(168,85,247,0.08)]',
  emerald: 'border-emerald-500/25 shadow-[0_0_40px_rgba(16,185,129,0.08)]',
};

interface Props {
  model: WorkspaceWindowModel;
  zIndex: number;
  minimized: boolean;
  onFocus: () => void;
  onClose: () => void;
  onToggleMinimize: () => void;
  onMove: (x: number, y: number) => void;
  onResize: (width: number, height: number) => void;
  children: React.ReactNode;
}

export const WorkspaceWindow: React.FC<Props> = ({
  model,
  zIndex,
  minimized,
  onFocus,
  onClose,
  onToggleMinimize,
  onMove,
  onResize,
  children,
}) => {
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const resizeRef = useRef<{ sx: number; sy: number; ow: number; oh: number } | null>(null);
  const accent = model.accent || 'cyan';
  const borderCls = accentBorder[accent] || accentBorder.cyan;

  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      onFocus();
      dragRef.current = { sx: e.clientX, sy: e.clientY, ox: model.x, oy: model.y };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [model.x, model.y, onFocus],
  );

  const onDragMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      onMove(d.ox + (e.clientX - d.sx), d.oy + (e.clientY - d.sy));
    },
    [onMove],
  );

  const onDragEnd = useCallback((e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      onFocus();
      resizeRef.current = { sx: e.clientX, sy: e.clientY, ow: model.width, oh: model.height };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [model.width, model.height, onFocus],
  );

  const onResizeMove = useCallback(
    (e: React.PointerEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      onResize(
        Math.max(280, r.ow + (e.clientX - r.sx)),
        Math.max(180, r.oh + (e.clientY - r.sy)),
      );
    },
    [onResize],
  );

  const onResizeEnd = useCallback((e: React.PointerEvent) => {
    resizeRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div
      data-workspace-window
      className={`absolute flex flex-col rounded-2xl border bg-[#0f172a]/92 backdrop-blur-md overflow-hidden ${borderCls}`}
      style={{
        left: model.x,
        top: model.y,
        width: model.width,
        height: minimized ? 44 : model.height,
        zIndex,
      }}
      onPointerDown={onFocus}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06] bg-slate-900/80 cursor-grab active:cursor-grabbing select-none"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
      >
        <GripHorizontal className="w-4 h-4 text-slate-600 shrink-0" />
        <span className="text-sm font-semibold text-white truncate flex-1">{model.title}</span>
        <PortalBadge label="Donna" accent="cyan" />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleMinimize();
          }}
          className="p-1 rounded-lg text-slate-500 hover:text-white hover:bg-white/5"
          aria-label={minimized ? 'Fenster öffnen' : 'Fenster minimieren'}
        >
          {minimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="p-1 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10"
          aria-label="Fenster schließen"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {!minimized && (
        <>
          <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
          <div
            className="absolute right-0 bottom-0 w-4 h-4 cursor-se-resize"
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            aria-hidden
          />
        </>
      )}
    </div>
  );
};
